import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import {
  brokerNotificationRowToUi,
  BROKER_NOTIFICATION_POPUP_STATES,
  BROKER_NOTIFICATION_STATES,
  groupNotificationsForDisplay,
  mergeNotifications,
  shouldDeliverBrokerNotificationPopup,
} from '../lib/broker/brokerNotificationRecords'

import { persistAlertPresentation } from '../lib/priceAlerts/presentation.js'
import { persistVwapNotificationState } from '../lib/chart/vwapNotifications.js'

const STORAGE_KEY = 'bwc-notifications'
const LEGACY_STORAGE_KEY = 'bwc-order-notifications'
const BROKER_POPUP_HYDRATION_MAX_AGE_MS = 24 * 60 * 60 * 1000
const REVIEW_REQUIRED_BROKER_NOTIFICATION_TYPES = new Set(['reject', 'stop_hit', 'position_closed'])
const REVIEW_REQUIRED_BROKER_NOTIFICATION_PRIORITIES = new Set(['high', 'critical'])
const SERVER_SELECT = [
  'notification_id',
  'broker_event_id',
  'user_id',
  'notification_type',
  'notification_state',
  'popup_state',
  'tone',
  'priority',
  'sticky',
  'symbol',
  'instrument_key',
  'title',
  'message',
  'details',
  'workspace_route',
  'fired_at',
  'read_at',
  'cleared_at',
  'archived_at',
  'created_at',
].join(', ')

const NotificationsContext = createContext({
  enabled: false,
  notifications: [],
  centerOpen: false,
  setCenterOpen: () => {},
  upsertNotification: () => {},
  dismissNotification: () => {},
  dismissNotificationPopup: () => {},
  markNotificationRead: () => {},
  clearNotificationHistory: () => {},
})

function loadStoredNotifications() {
  if (typeof window === 'undefined') return []
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) || window.localStorage.getItem(LEGACY_STORAGE_KEY) || '[]'
    const parsed = JSON.parse(stored)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function showBrowserNotification(notification) {
  if (typeof window === 'undefined') return
  const BrowserNotification = window.Notification
  if (!BrowserNotification || notification.browser !== true) return

  const show = () => {
    try {
      new BrowserNotification(notification.title || 'Erevis notification', {
        body: notification.browserBody || notification.message || '',
        tag: notification.id,
        data: { symbol: notification.symbol || null, type: notification.type || null },
      })
    } catch {
      // Browser notifications are additive; in-app notification history is authoritative.
    }
  }

  if (BrowserNotification.permission === 'granted') {
    show()
    return
  }
  if (BrowserNotification.permission === 'default' && typeof BrowserNotification.requestPermission === 'function') {
    BrowserNotification.requestPermission().then((permission) => {
      if (permission === 'granted') show()
    }).catch(() => {})
  }
}

function isStaleHydrationNotification(row = {}) {
  const value = row.fired_at || row.created_at
  if (!value) return true
  const firedAt = new Date(value).getTime()
  if (!Number.isFinite(firedAt)) return true
  return Date.now() - firedAt > BROKER_POPUP_HYDRATION_MAX_AGE_MS
}

function requiresBrokerNotificationReview(row = {}) {
  const type = String(row.notification_type || '').trim().toLowerCase()
  const tone = String(row.tone || '').trim().toLowerCase()
  const priority = String(row.priority || '').trim().toLowerCase()
  return (
    tone === 'error' ||
    REVIEW_REQUIRED_BROKER_NOTIFICATION_TYPES.has(type) ||
    REVIEW_REQUIRED_BROKER_NOTIFICATION_PRIORITIES.has(priority)
  )
}

function shouldHydrateBrokerNotificationAsRead(row = {}) {
  return (
    row.notification_state === BROKER_NOTIFICATION_STATES.ACTIVE &&
    row.popup_state !== BROKER_NOTIFICATION_POPUP_STATES.PENDING &&
    !requiresBrokerNotificationReview(row)
  )
}

function notificationActionIds(id, displayNotifications = []) {
  const notification = displayNotifications.find(item => item.id === id)
  if (Array.isArray(notification?.sourceNotificationIds) && notification.sourceNotificationIds.length) {
    return notification.sourceNotificationIds
  }
  return id ? [id] : []
}

function updateNotificationIds(query, ids = []) {
  if (ids.length === 1) return query.eq('notification_id', ids[0])
  return query.in('notification_id', ids)
}

export function NotificationsProvider({ children }) {
  const auth = useAuth() || {}
  const userId = auth.user?.id || null
  const [notifications, setNotifications] = useState(loadStoredNotifications)
  const [centerOpen, setCenterOpen] = useState(false)
  const displayNotifications = useMemo(
    () => groupNotificationsForDisplay(notifications.filter(n => !['vwap','price-alert'].includes(n.source) || n.ownerUserId === userId)),
    [notifications, userId]
  )
  const knownNotificationIdsRef = useRef(new Set(notifications.map(n => n.id).filter(Boolean)))
  const browserNotifiedIdsRef = useRef(new Set())

  useEffect(() => {
    try {
      const localNotifications = notifications.filter(n => n.serverBacked !== true && n.source !== 'price-alert').slice(0, 50)
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(localNotifications))
      window.localStorage.removeItem(LEGACY_STORAGE_KEY)
    } catch {
      // Non-critical UI history cache.
    }
  }, [notifications])

  useEffect(() => {
    if (userId) return
    setNotifications(prev => prev.filter(n => n.serverBacked !== true))
  }, [userId])

  useEffect(() => { persistAlertPresentation(userId, notifications) }, [userId, notifications])

  const upsertNotification = useCallback((notification) => {
    if (!notification?.id) return
    const isNew = !knownNotificationIdsRef.current.has(notification.id)
    knownNotificationIdsRef.current.add(notification.id)
    const firedAt = notification.firedAt || notification.createdAt || new Date().toISOString()
    setNotifications(prev => {
      const existing = prev.find(n => n.id === notification.id)
      const nextNotification = existing
        ? {
            ...existing,
            ...notification,
            read: notification.source === 'vwap' ? existing.read || notification.read === true : existing.read,
            dismissed: notification.source === 'vwap' ? existing.dismissed || notification.dismissed === true : existing.dismissed,
            cleared: notification.source === 'vwap' ? existing.cleared || notification.cleared === true : existing.cleared,
            popupDismissed: notification.source === 'vwap' ? existing.popupDismissed || notification.popupDismissed === true : existing.popupDismissed,
            createdAt: existing.createdAt || existing.firedAt || firedAt,
            firedAt: existing.firedAt || existing.createdAt || firedAt,
          }
        : { read: false, dismissed: false, cleared: false, createdAt: firedAt, firedAt, ...notification }
      const next = [nextNotification, ...prev.filter(n => n.id !== notification.id)]
      return next.slice(0, 50)
    })
    if (isNew && notification.browser === true && !browserNotifiedIdsRef.current.has(notification.id)) {
      browserNotifiedIdsRef.current.add(notification.id)
      showBrowserNotification(notification)
    }
  }, [])

  const markServerPopupsDelivered = useCallback(async (ids = []) => {
    const notificationIds = ids.filter(Boolean)
    if (!notificationIds.length || !userId) return
    try {
      await supabase
        .from('broker_event_notifications')
        .update({
          popup_state: BROKER_NOTIFICATION_POPUP_STATES.DELIVERED,
          popup_delivered_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId)
        .in('notification_id', notificationIds)
    } catch {
      // Popup delivery ack is best-effort; polling will retry if needed.
    }
  }, [userId])

  const markServerPopupsRead = useCallback(async (ids = []) => {
    const notificationIds = ids.filter(Boolean)
    if (!notificationIds.length || !userId) return
    const now = new Date().toISOString()
    try {
      await supabase
        .from('broker_event_notifications')
        .update({
          notification_state: BROKER_NOTIFICATION_STATES.READ,
          popup_state: BROKER_NOTIFICATION_POPUP_STATES.DELIVERED,
          read_at: now,
          popup_delivered_at: now,
          updated_at: now,
        })
        .eq('user_id', userId)
        .in('notification_id', notificationIds)
        .eq('notification_state', BROKER_NOTIFICATION_STATES.ACTIVE)
    } catch {
      // Stale popup suppression is best-effort; UI classification still prevents a replay.
    }
  }, [userId])

  const applyServerRows = useCallback((rows = [], { suppressStalePendingPopups = false } = {}) => {
    const staleBrokerNotificationIds = new Set(
      suppressStalePendingPopups
        ? rows
            .filter(row => shouldDeliverBrokerNotificationPopup(row) && isStaleHydrationNotification(row))
            .map(row => row.notification_id)
            .filter(Boolean)
        : []
    )
    const readOnHydrationBrokerNotificationIds = new Set(
      suppressStalePendingPopups
        ? rows
            .filter(row => shouldHydrateBrokerNotificationAsRead(row) || (
              staleBrokerNotificationIds.has(row.notification_id) && !requiresBrokerNotificationReview(row)
            ))
            .map(row => row.notification_id)
            .filter(Boolean)
        : []
    )
    const deliveredOnHydrationBrokerNotificationIds = new Set(
      suppressStalePendingPopups
        ? rows
            .filter(row => staleBrokerNotificationIds.has(row.notification_id) && requiresBrokerNotificationReview(row))
            .map(row => row.notification_id)
            .filter(Boolean)
        : []
    )
    setNotifications(prev => {
      const localNotifications = prev.filter(n => n.serverBacked !== true)
      const previousServerNotifications = new Map(
        prev.filter(n => n.serverBacked === true).map(n => [n.id, n])
      )
      const serverNotifications = rows.map((row) => {
        const incoming = brokerNotificationRowToUi(row)
        if (readOnHydrationBrokerNotificationIds.has(incoming.id)) {
          incoming.read = true
          incoming.dismissed = staleBrokerNotificationIds.has(incoming.id)
          incoming.popupDismissed = true
          incoming.notificationState = BROKER_NOTIFICATION_STATES.READ
          incoming.popupState = BROKER_NOTIFICATION_POPUP_STATES.DELIVERED
        } else if (staleBrokerNotificationIds.has(incoming.id)) {
          incoming.dismissed = true
          incoming.popupDismissed = true
          incoming.popupState = BROKER_NOTIFICATION_POPUP_STATES.DELIVERED
        }
        const previous = previousServerNotifications.get(incoming.id)
        if (!previous) return incoming
        const closed = incoming.notificationState === BROKER_NOTIFICATION_STATES.CLEARED ||
          incoming.notificationState === BROKER_NOTIFICATION_STATES.ARCHIVED
        return {
          ...previous,
          ...incoming,
          read: previous.read || incoming.read,
          dismissed: closed
            ? true
            : previous.dismissed === false
              ? false
              : incoming.dismissed,
          popupDismissed: closed
            ? true
            : previous.popupDismissed === false
              ? false
              : incoming.popupDismissed,
        }
      })
      return mergeNotifications(localNotifications, serverNotifications)
    })
    const popupIds = rows
      .filter(row => shouldDeliverBrokerNotificationPopup(row) && !staleBrokerNotificationIds.has(row.notification_id))
      .map(row => row.notification_id)
    markServerPopupsDelivered([...popupIds, ...deliveredOnHydrationBrokerNotificationIds])
    markServerPopupsRead([...readOnHydrationBrokerNotificationIds])
  }, [markServerPopupsDelivered, markServerPopupsRead])

  useEffect(() => {
    if (!userId) return undefined
    let cancelled = false
    ;(async () => {
      const { data, error } = await supabase
        .from('broker_event_notifications')
        .select(SERVER_SELECT)
        .eq('user_id', userId)
        .neq('notification_state', BROKER_NOTIFICATION_STATES.ARCHIVED)
        .order('fired_at', { ascending: false })
        .limit(50)
      if (cancelled || error) return
      applyServerRows(data || [], { suppressStalePendingPopups: true })
    })()
    return () => { cancelled = true }
  }, [applyServerRows, userId])

  useEffect(() => {
    if (!userId) return undefined
    const channel = supabase
      .channel(`broker_event_notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'broker_event_notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload?.new
          if (!row || row.notification_state === BROKER_NOTIFICATION_STATES.ARCHIVED) return
          applyServerRows([row])
        }
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [applyServerRows, userId])

  const dismissNotification = useCallback((id) => {
    const targetIds = notificationActionIds(id, displayNotifications)
    const targetIdSet = new Set(targetIds)
    persistVwapNotificationState(notifications.filter(n => targetIdSet.has(n.id) && n.ownerUserId === userId), {read:true,dismissed:true,cleared:true,popupDismissed:true})
    const serverBacked = notifications.some(n => targetIdSet.has(n.id) && n.serverBacked === true)
    const clearedAt = new Date().toISOString()
    setNotifications(prev => prev.map(n => {
      if (!targetIdSet.has(n.id)) return n
      if (n.serverBacked === true) {
        return {
          ...n,
          dismissed: true,
          read: true,
          notificationState: BROKER_NOTIFICATION_STATES.CLEARED,
          popupState: BROKER_NOTIFICATION_POPUP_STATES.DELIVERED,
        }
      }
      return { ...n, dismissed: true, read: true, cleared: true, clearedAt }
    }))
    if (serverBacked && userId && targetIds.length) {
      updateNotificationIds(supabase
        .from('broker_event_notifications')
        .update({
          notification_state: BROKER_NOTIFICATION_STATES.CLEARED,
          popup_state: BROKER_NOTIFICATION_POPUP_STATES.DELIVERED,
          read_at: new Date().toISOString(),
          cleared_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId), targetIds)
        .then(() => {})
    }
  }, [displayNotifications, notifications, userId])

  const dismissNotificationPopup = useCallback((id) => {
    const targetIds = notificationActionIds(id, displayNotifications)
    const targetIdSet = new Set(targetIds)
    persistVwapNotificationState(notifications.filter(n => targetIdSet.has(n.id) && n.ownerUserId === userId), {popupDismissed:true})
    const serverBacked = notifications.some(n => targetIdSet.has(n.id) && n.serverBacked === true)
    setNotifications(prev => prev.map(n => {
      if (!targetIdSet.has(n.id)) return n
      return {
        ...n,
        popupDismissed: true,
        popupState: n.serverBacked === true ? BROKER_NOTIFICATION_POPUP_STATES.DELIVERED : n.popupState,
      }
    }))
    if (serverBacked && userId && targetIds.length) {
      const now = new Date().toISOString()
      updateNotificationIds(supabase
        .from('broker_event_notifications')
        .update({
          popup_state: BROKER_NOTIFICATION_POPUP_STATES.DELIVERED,
          popup_delivered_at: now,
          updated_at: now,
        })
        .eq('user_id', userId), targetIds)
        .then(() => {})
    }
  }, [displayNotifications, notifications, userId])

  const markNotificationRead = useCallback((id) => {
    const targetIds = notificationActionIds(id, displayNotifications)
    const targetIdSet = new Set(targetIds)
    persistVwapNotificationState(notifications.filter(n => targetIdSet.has(n.id) && n.ownerUserId === userId), {read:true})
    const serverBacked = notifications.some(n => targetIdSet.has(n.id) && n.serverBacked === true)
    setNotifications(prev => prev.map(n => {
      if (!targetIdSet.has(n.id)) return n
      return {
        ...n,
        read: true,
        notificationState: n.notificationState === BROKER_NOTIFICATION_STATES.ACTIVE
          ? BROKER_NOTIFICATION_STATES.READ
          : n.notificationState,
      }
    }))
    if (serverBacked && userId && targetIds.length) {
      updateNotificationIds(supabase
        .from('broker_event_notifications')
        .update({
          notification_state: BROKER_NOTIFICATION_STATES.READ,
          read_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId), targetIds)
        .eq('notification_state', BROKER_NOTIFICATION_STATES.ACTIVE)
        .then(() => {})
    }
  }, [displayNotifications, notifications, userId])

  const clearNotificationHistory = useCallback(() => {
    persistVwapNotificationState(notifications.filter(n => n.ownerUserId === userId), {read:true,dismissed:true,cleared:true,popupDismissed:true})
    const archivedIds = notifications
      .filter(n => n.serverBacked === true && (n.tone !== 'error' || n.dismissed === true))
      .map(n => n.id)
    const clearedAt = new Date().toISOString()
    setNotifications(prev => prev.flatMap(n => {
      if (n.serverBacked === true) {
        return n.tone === 'error' && n.dismissed !== true ? [n] : []
      }
      return [{ ...n, dismissed: true, read: true, cleared: true, clearedAt: n.clearedAt || clearedAt }]
    }))
    if (archivedIds.length && userId) {
      const now = new Date().toISOString()
      supabase
        .from('broker_event_notifications')
        .update({
          notification_state: BROKER_NOTIFICATION_STATES.ARCHIVED,
          read_at: now,
          archived_at: now,
          updated_at: now,
        })
        .eq('user_id', userId)
        .in('notification_id', archivedIds)
        .then(() => {})
    }
  }, [notifications, userId])

  const value = useMemo(() => ({
    enabled: true,
    notifications: displayNotifications,
    centerOpen,
    setCenterOpen,
    upsertNotification,
    upsertOrderNotification: upsertNotification,
    dismissNotification,
    dismissNotificationPopup,
    markNotificationRead,
    clearNotificationHistory,
  }), [
    displayNotifications,
    centerOpen,
    upsertNotification,
    dismissNotification,
    dismissNotificationPopup,
    markNotificationRead,
    clearNotificationHistory,
  ])

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  )
}

export function useNotifications() {
  return useContext(NotificationsContext)
}

export const OrderNotificationsProvider = NotificationsProvider
export const useOrderNotifications = useNotifications
